// File: scholarship-frontend/src/pages/pm/PMDashboard.tsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, Search, SlidersHorizontal, X } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import {
  fetchPMDashboard,
  fetchPMFlags,
  fetchPMSubmissions,
  PMDashboardStats,
  PMFlagItem,
  PMSubmissionQueueItem,
  submitPMSupervisoryIntervention,
} from "@/api/pmApi";
import {
  AvailableAction,
  fetchAvailableActions,
  formatWorkflowActionLabel,
} from "@/api/workflowApi";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { formatWorkflowStatusLabel } from "@/utils/semanticLabels";

type SavedView = "all" | "urgent" | "unassigned" | "stale" | "actionable" | "blocked";
type SortDirection = "asc" | "desc";
type SortKey =
  | "priority"
  | "submission"
  | "program"
  | "status"
  | "assignment"
  | "blocking"
  | "age"
  | "actions";

interface InterventionDraft {
  actions: AvailableAction[];
  actionCode: string;
  reason: string;
  loading: boolean;
  submitting: boolean;
  error: string;
}

interface PriorityMeta {
  label: string;
  rank: number;
  className: string;
}

const emptyDraft = (): InterventionDraft => ({
  actions: [],
  actionCode: "",
  reason: "",
  loading: false,
  submitting: false,
  error: "",
});

function formatProjectionLabel(value: string | null | undefined): string {
  return formatWorkflowStatusLabel(value);
}

function normalize(value: string | number | null | undefined): string {
  return String(value ?? "").trim().toLowerCase();
}

function sortLabel(value: string | number | null | undefined): string {
  return normalize(formatProjectionLabel(String(value ?? "")));
}

function assignmentLabel(item: PMSubmissionQueueItem): string {
  if (item.assignment_state === "unassigned") return "Unassigned";
  if (item.assignment_state === "stale") return "Stale assignment";
  if (item.assigned_to_username) return item.assigned_to_username;
  return formatProjectionLabel(item.assignment_state || "Assigned");
}

function ageLabel(item: PMSubmissionQueueItem): string {
  if (item.assignment_age_days === null || item.assignment_age_days === undefined) {
    return "-";
  }
  if (item.assignment_age_days === 0) return "Today";
  return `${item.assignment_age_days}d`;
}

function hasBlockingSignal(item: PMSubmissionQueueItem): boolean {
  const blocking = normalize(item.blocking_reason);
  return Boolean(item.locked) ||
    normalize(item.actionability_state).includes("blocked") ||
    Boolean(blocking && !["none", "not applicable", "no blocker", "-"].includes(blocking));
}

function priorityMeta(item: PMSubmissionQueueItem): PriorityMeta {
  const blocking = normalize(item.blocking_reason);

  if (item.locked || blocking.includes("locked")) {
    return {
      label: "Locked",
      rank: 4,
      className: "border-red-300 bg-red-50 text-red-800",
    };
  }

  if (item.assignment_state === "unassigned" || item.assignment_state === "stale") {
    return {
      label: "High",
      rank: 3,
      className: "border-red-300 bg-red-50 text-red-800",
    };
  }

  if (hasBlockingSignal(item)) {
    return {
      label: "Needs review",
      rank: 2,
      className: "border-amber-300 bg-amber-50 text-amber-800",
    };
  }

  if (item.executable_action_count > 0) {
    return {
      label: "Actionable",
      rank: 1,
      className: "border-blue-300 bg-blue-50 text-blue-800",
    };
  }

  return {
    label: "Monitor",
    rank: 0,
    className: "border-slate-300 bg-slate-50 text-slate-700",
  };
}

function viewMatches(item: PMSubmissionQueueItem, view: SavedView): boolean {
  if (view === "all") return true;
  if (view === "urgent") return priorityMeta(item).rank >= 2;
  if (view === "unassigned") return item.assignment_state === "unassigned";
  if (view === "stale") return item.assignment_state === "stale";
  if (view === "actionable") return item.executable_action_count > 0;
  if (view === "blocked") return hasBlockingSignal(item);
  return true;
}

function compareItems(
  a: PMSubmissionQueueItem,
  b: PMSubmissionQueueItem,
  sortKey: SortKey
): number {
  if (sortKey === "priority") return priorityMeta(a).rank - priorityMeta(b).rank;
  if (sortKey === "submission") return a.submission_id - b.submission_id;
  if (sortKey === "program") return sortLabel(a.program_code).localeCompare(sortLabel(b.program_code));
  if (sortKey === "status") return sortLabel(a.status).localeCompare(sortLabel(b.status));
  if (sortKey === "assignment") return sortLabel(assignmentLabel(a)).localeCompare(sortLabel(assignmentLabel(b)));
  if (sortKey === "blocking") return sortLabel(a.blocking_reason).localeCompare(sortLabel(b.blocking_reason));
  if (sortKey === "age") return (a.assignment_age_days ?? -1) - (b.assignment_age_days ?? -1);
  if (sortKey === "actions") return a.executable_action_count - b.executable_action_count;
  return 0;
}

const PMDashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState<PMDashboardStats | null>(null);
  const [queue, setQueue] = useState<PMSubmissionQueueItem[]>([]);
  const [queueTotal, setQueueTotal] = useState<number>(0);
  const [flags, setFlags] = useState<PMFlagItem[]>([]);
  const [error, setError] = useState<string>("");
  const [drafts, setDrafts] = useState<Record<number, InterventionDraft>>({});
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [savedView, setSavedView] = useState<SavedView>(
    searchParams.get("view") === "exceptions" ? "urgent" : "all"
  );
  const [programFilter, setProgramFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [assignmentFilter, setAssignmentFilter] = useState("all");
  const [sortKey, setSortKey] = useState<SortKey>("priority");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const selectedSubmission = useMemo(
    () => queue.find((item) => item.submission_id === selectedSubmissionId) ?? null,
    [queue, selectedSubmissionId]
  );

  const requestedSubmissionId = useMemo(() => {
    const raw = searchParams.get("submission");
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [searchParams]);

  const updateDraft = useCallback(
    (submissionId: number, patch: Partial<InterventionDraft>) => {
      setDrafts((prev) => ({
        ...prev,
        [submissionId]: {
          ...(prev[submissionId] ?? emptyDraft()),
          ...patch,
        },
      }));
    },
    []
  );

  const fetchStats = useCallback(async () => {
    try {
      setError("");
      const [dashboardRes, queueRes, flagsRes] = await Promise.all([
        fetchPMDashboard(),
        fetchPMSubmissions({ limit: 200 }),
        fetchPMFlags(),
      ]);
      setStats(dashboardRes);
      setQueue(queueRes.items);
      setQueueTotal(queueRes.total);
      setFlags(flagsRes.items);
    } catch (err) {
      console.error(err);
      setError("Failed to load Program Manager workbench.");
    }
  }, []);

  const loadInterventionActions = useCallback(
    async (submissionId: number) => {
      updateDraft(submissionId, {
        loading: true,
        error: "",
        actions: [],
        actionCode: "",
      });
      try {
        const response = await fetchAvailableActions(submissionId);
        updateDraft(submissionId, {
          actions: response.actions ?? [],
          actionCode: "",
          loading: false,
        });
      } catch (err: any) {
        updateDraft(submissionId, {
          loading: false,
          error: err?.response?.data?.detail ?? "Failed to load backend actions.",
        });
      }
    },
    [updateDraft]
  );

  const openActionPanel = useCallback(
    (submissionId: number) => {
      setSelectedSubmissionId(submissionId);
      const next = new URLSearchParams(searchParams);
      next.set("submission", String(submissionId));
      setSearchParams(next);
      void loadInterventionActions(submissionId);
    },
    [loadInterventionActions, searchParams, setSearchParams]
  );

  const closeActionPanel = () => {
    setSelectedSubmissionId(null);
    const next = new URLSearchParams(searchParams);
    next.delete("submission");
    next.delete("view");
    setSearchParams(next);
  };

  const submitIntervention = async (submissionId: number) => {
    const draft = drafts[submissionId] ?? emptyDraft();
    const reason = draft.reason.trim();
    if (!draft.actionCode || !reason) return;
    if (!draft.actions.some((action) => action.action_code === draft.actionCode)) {
      updateDraft(submissionId, {
        error: "Workflow state changed. Reload backend actions.",
      });
      return;
    }

    updateDraft(submissionId, { submitting: true, error: "" });
    try {
      await submitPMSupervisoryIntervention(submissionId, {
        action_code: draft.actionCode,
        intervention_reason: reason,
      });
      setDrafts((prev) => ({
        ...prev,
        [submissionId]: emptyDraft(),
      }));
      closeActionPanel();
      await fetchStats();
    } catch (err: any) {
      updateDraft(submissionId, {
        submitting: false,
        error: err?.response?.data?.detail ?? "Supervisory intervention failed.",
      });
    }
  };

  useEffect(() => {
    void fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (!requestedSubmissionId || queue.length === 0) return;
    if (!queue.some((item) => item.submission_id === requestedSubmissionId)) return;
    if (selectedSubmissionId === requestedSubmissionId) return;
    setSelectedSubmissionId(requestedSubmissionId);
    void loadInterventionActions(requestedSubmissionId);
  }, [loadInterventionActions, queue, requestedSubmissionId, selectedSubmissionId]);

  const programOptions = useMemo(
    () =>
      Array.from(new Set(queue.map((item) => item.program_code || "No program"))).sort(),
    [queue]
  );

  const statusOptions = useMemo(
    () => Array.from(new Set(queue.map((item) => item.status))).sort(),
    [queue]
  );

  const assignmentOptions = useMemo(
    () => Array.from(new Set(queue.map((item) => item.assignment_state))).sort(),
    [queue]
  );

  const filteredQueue = useMemo(() => {
    const query = search.trim().toLowerCase();
    return queue
      .filter((item) => viewMatches(item, savedView))
      .filter((item) => programFilter === "all" || (item.program_code || "No program") === programFilter)
      .filter((item) => statusFilter === "all" || item.status === statusFilter)
      .filter((item) => assignmentFilter === "all" || item.assignment_state === assignmentFilter)
      .filter((item) => {
        if (!query) return true;
        return [
          item.submission_id,
          item.program_code,
          item.academic_year,
          item.status,
          item.assigned_to_username,
          item.assignment_state,
          item.assignment_source,
          item.routing_reason,
          item.lifecycle_phase,
          item.review_state,
          item.latest_governance_event,
          item.latest_return_source,
          item.actionability_state,
          item.blocking_reason,
        ]
          .join(" ")
          .toLowerCase()
          .includes(query);
      })
      .sort((a, b) => {
        const result = compareItems(a, b, sortKey);
        return sortDirection === "asc" ? result : -result;
      });
  }, [assignmentFilter, programFilter, queue, savedView, search, sortDirection, sortKey, statusFilter]);

  const kpis = useMemo(() => {
    return [
      {
        label: "Queue",
        value: queueTotal,
        view: "all" as SavedView,
        className: "border-slate-300 bg-white text-slate-900",
      },
      {
        label: "Urgent",
        value: queue.filter((item) => priorityMeta(item).rank >= 2).length,
        view: "urgent" as SavedView,
        className: "border-amber-300 bg-amber-50 text-amber-900",
      },
      {
        label: "Unassigned",
        value: stats?.unassignedSubmissions ?? 0,
        view: "unassigned" as SavedView,
        className: "border-red-300 bg-red-50 text-red-800",
      },
      {
        label: "Stale",
        value: queue.filter((item) => item.assignment_state === "stale").length,
        view: "stale" as SavedView,
        className: "border-amber-300 bg-amber-50 text-amber-900",
      },
      {
        label: "Actionable",
        value: queue.filter((item) => item.executable_action_count > 0).length,
        view: "actionable" as SavedView,
        className: "border-blue-300 bg-blue-50 text-blue-800",
      },
      {
        label: "Blocked",
        value: queue.filter((item) => hasBlockingSignal(item)).length,
        view: "blocked" as SavedView,
        className: "border-violet-300 bg-violet-50 text-violet-800",
      },
    ];
  }, [queue, queueTotal, stats]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection(key === "priority" || key === "actions" || key === "age" ? "desc" : "asc");
  };

  const clearFilters = () => {
    setSearch("");
    setSavedView("all");
    setProgramFilter("all");
    setStatusFilter("all");
    setAssignmentFilter("all");
    setSortKey("priority");
    setSortDirection("desc");
  };

  const tableHeader = (label: string, key: SortKey, className = "") => (
    <button
      type="button"
      className={`flex w-full items-center gap-1 text-left ${className}`}
      onClick={() => toggleSort(key)}
    >
      {label}
      <span className="text-[10px] text-muted-foreground">
        {sortKey === key ? (sortDirection === "asc" ? "▲" : "▼") : ""}
      </span>
    </button>
  );

  const selectedDraft = selectedSubmissionId ? drafts[selectedSubmissionId] ?? emptyDraft() : emptyDraft();

  return (
    <div className="w-full max-w-[1440px] space-y-3 overflow-x-hidden p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-950">PM Operations Workbench</h1>
          <div className="text-xs text-muted-foreground">
            {filteredQueue.length} visible of {queueTotal} PM-visible submissions
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => void fetchStats()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      {!stats && !error ? (
        <div className="space-y-3">
          <Skeleton className="h-12 w-full rounded-md" />
          <Skeleton className="h-[360px] w-full rounded-md" />
        </div>
      ) : null}

      {stats ? (
        <>
          <div className="flex flex-wrap items-center gap-2 rounded-md border bg-white p-2">
            <div className="relative min-w-[240px] flex-1 basis-[340px] lg:max-w-[520px]">
              <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search submission, program, DDO, blocker"
                className="h-9 pl-8"
              />
            </div>
            <select
              value={savedView}
              onChange={(event) => setSavedView(event.target.value as SavedView)}
              className="h-9 w-[150px] rounded-md border border-gray-300 bg-white px-2 text-sm"
              aria-label="Saved view"
            >
              <option value="all">All PM work</option>
              <option value="urgent">Urgent</option>
              <option value="unassigned">Unassigned</option>
              <option value="stale">Stale</option>
              <option value="actionable">Actionable</option>
              <option value="blocked">Blocked / flags</option>
            </select>
            <select
              value={programFilter}
              onChange={(event) => setProgramFilter(event.target.value)}
              className="h-9 w-[150px] rounded-md border border-gray-300 bg-white px-2 text-sm"
              aria-label="Program filter"
            >
              <option value="all">All programs</option>
              {programOptions.map((program) => (
                <option key={program} value={program}>
                  {program}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="h-9 w-[150px] rounded-md border border-gray-300 bg-white px-2 text-sm"
              aria-label="Status filter"
            >
              <option value="all">All statuses</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {formatProjectionLabel(status)}
                </option>
              ))}
            </select>
            <select
              value={assignmentFilter}
              onChange={(event) => setAssignmentFilter(event.target.value)}
              className="h-9 w-[160px] rounded-md border border-gray-300 bg-white px-2 text-sm"
              aria-label="Assignment filter"
            >
              <option value="all">All assignments</option>
              {assignmentOptions.map((assignment) => (
                <option key={assignment} value={assignment}>
                  {formatProjectionLabel(assignment)}
                </option>
              ))}
            </select>
            <Button variant="outline" size="sm" onClick={clearFilters}>
              <SlidersHorizontal className="mr-2 h-4 w-4" />
              Reset
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            {kpis.map((kpi) => (
              <button
                key={kpi.label}
                type="button"
                onClick={() => setSavedView(kpi.view)}
                className={`min-w-[104px] rounded-md border px-3 py-1.5 text-left transition hover:shadow-sm ${
                  savedView === kpi.view ? "ring-2 ring-indigo-500" : ""
                } ${kpi.className}`}
              >
                <div className="text-[11px] font-medium uppercase tracking-wide">
                  {kpi.label}
                </div>
                <div className="text-lg font-semibold leading-5">{kpi.value}</div>
              </button>
            ))}
          </div>

          {flags.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2 rounded-md border bg-white px-3 py-2">
              <span className="text-xs font-semibold uppercase text-muted-foreground">
                Pilot flags
              </span>
              {flags.map((flag) => (
                <Badge
                  key={flag.code}
                  variant="outline"
                  className={flag.severity === "high" ? "border-red-300 text-red-800" : ""}
                >
                  {flag.label}: {flag.count}
                </Badge>
              ))}
            </div>
          ) : null}

          <div className="rounded-md border bg-white">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2">
              <div>
                <div className="text-sm font-semibold text-gray-950">Submissions for Review</div>
                <div className="text-xs text-muted-foreground">
                  Filter, sort, and open submissions that need PM attention.
                </div>
              </div>
              <Badge variant="outline">{filteredQueue.length} rows</Badge>
            </div>

            {filteredQueue.length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                No submissions match the active PM workbench filters.
              </div>
            ) : (
              <div className="max-h-[68vh] overflow-y-auto overflow-x-hidden">
                <Table className="w-full table-fixed">
                  <TableHeader className="sticky top-0 z-20 bg-slate-50 shadow-sm">
                    <TableRow>
                      <TableHead className="w-[78px]">{tableHeader("Priority", "priority")}</TableHead>
                      <TableHead className="w-[76px]">{tableHeader("Sub.", "submission")}</TableHead>
                      <TableHead className="hidden w-[112px] xl:table-cell">{tableHeader("Program", "program")}</TableHead>
                      <TableHead className="w-[126px]">{tableHeader("State", "status")}</TableHead>
                      <TableHead className="w-[146px]">{tableHeader("Assignment", "assignment")}</TableHead>
                      <TableHead>{tableHeader("Blocking reason", "blocking")}</TableHead>
                      <TableHead className="hidden w-[152px] 2xl:table-cell">Latest event</TableHead>
                      <TableHead className="w-[58px]">{tableHeader("Age", "age")}</TableHead>
                      <TableHead className="sticky right-0 z-30 w-[104px] bg-slate-50 text-right">
                        {tableHeader("Actions", "actions", "justify-end")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredQueue.map((item) => {
                      const priority = priorityMeta(item);
                      const selected = selectedSubmissionId === item.submission_id;
                      return (
                        <TableRow
                          key={item.submission_id}
                          className={selected ? "bg-indigo-50 hover:bg-indigo-50" : ""}
                        >
                          <TableCell className="px-2 py-2">
                            <Badge variant="outline" className={priority.className}>
                              {priority.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="px-2 py-2 font-medium">
                            #{item.submission_id}
                          </TableCell>
                          <TableCell className="hidden px-2 py-2 xl:table-cell">
                            <div className="truncate">{item.program_code || "No program"}</div>
                            <div className="text-[11px] text-muted-foreground">
                              {item.academic_year ?? "-"}
                            </div>
                          </TableCell>
                          <TableCell className="px-2 py-2">
                            <div className="truncate text-sm font-medium">
                              {formatProjectionLabel(item.status)}
                            </div>
                            <div className="truncate text-[11px] text-muted-foreground">
                              <span className="xl:hidden">
                                {item.program_code || "No program"} ·{" "}
                              </span>
                              {formatProjectionLabel(item.lifecycle_phase)}
                            </div>
                          </TableCell>
                          <TableCell className="px-2 py-2">
                            <div className="truncate text-sm">{assignmentLabel(item)}</div>
                            <div className="truncate text-[11px] text-muted-foreground">
                              {formatProjectionLabel(item.assignment_source || item.assignment_state)}
                            </div>
                          </TableCell>
                          <TableCell className="px-2 py-2">
                            <div className="truncate text-sm">
                              {formatProjectionLabel(item.blocking_reason)}
                            </div>
                            <div className="truncate text-[11px] text-muted-foreground">
                              {formatProjectionLabel(item.actionability_state)}
                            </div>
                          </TableCell>
                          <TableCell className="hidden px-2 py-2 2xl:table-cell">
                            <div className="truncate text-sm">
                              {formatProjectionLabel(item.latest_governance_event)}
                            </div>
                            <div className="truncate text-[11px] text-muted-foreground">
                              {item.latest_return_source
                                ? `from ${formatProjectionLabel(item.latest_return_source)}`
                                : formatProjectionLabel(item.review_state)}
                            </div>
                          </TableCell>
                          <TableCell className="px-2 py-2 text-sm">{ageLabel(item)}</TableCell>
                          <TableCell className="sticky right-0 z-10 bg-white px-2 py-2 text-right shadow-[-8px_0_12px_-12px_rgba(15,23,42,0.45)]">
                            <div className="flex items-center justify-end gap-2">
                              <Badge variant="outline">{item.executable_action_count}</Badge>
                              <Button
                                size="sm"
                                variant={item.executable_action_count > 0 ? "default" : "outline"}
                                onClick={() => openActionPanel(item.submission_id)}
                              >
                                Act
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </>
      ) : null}

      {selectedSubmission ? (
        <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col border-l bg-white shadow-2xl">
          <div className="border-b px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-gray-950">
                  Submission #{selectedSubmission.submission_id}
                </div>
                <div className="text-xs text-muted-foreground">
                  {selectedSubmission.program_code || "No program"} ·{" "}
                  {formatProjectionLabel(selectedSubmission.status)}
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={closeActionPanel}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto px-4 py-3">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-md border bg-slate-50 p-2">
                <div className="text-[11px] uppercase text-muted-foreground">Assignment</div>
                <div className="truncate font-medium">{assignmentLabel(selectedSubmission)}</div>
              </div>
              <div className="rounded-md border bg-slate-50 p-2">
                <div className="text-[11px] uppercase text-muted-foreground">Age</div>
                <div className="font-medium">{ageLabel(selectedSubmission)}</div>
              </div>
              <div className="rounded-md border bg-slate-50 p-2">
                <div className="text-[11px] uppercase text-muted-foreground">Review state</div>
                <div className="truncate font-medium">
                  {formatProjectionLabel(selectedSubmission.review_state)}
                </div>
              </div>
              <div className="rounded-md border bg-slate-50 p-2">
                <div className="text-[11px] uppercase text-muted-foreground">Backend actions</div>
                <div className="font-medium">{selectedSubmission.executable_action_count}</div>
              </div>
            </div>

            <div className="rounded-md border p-3 text-sm">
              <div className="text-[11px] font-semibold uppercase text-muted-foreground">
                Blocking reason
              </div>
              <div className="mt-1">{formatProjectionLabel(selectedSubmission.blocking_reason)}</div>
              {selectedSubmission.routing_reason ? (
                <div className="mt-2 text-xs text-muted-foreground">
                  Routing: {selectedSubmission.routing_reason}
                </div>
              ) : null}
            </div>

            <div className="rounded-md border p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold">Available workflow actions</div>
                  <div className="text-xs text-muted-foreground">
                    Loaded from backend available-actions authority.
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={selectedDraft.loading}
                  onClick={() => loadInterventionActions(selectedSubmission.submission_id)}
                >
                  {selectedDraft.loading ? "Loading" : "Reload"}
                </Button>
              </div>

              {selectedDraft.error ? (
                <div className="rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-800">
                  {selectedDraft.error}
                </div>
              ) : null}

              {!selectedDraft.loading && selectedDraft.actions.length === 0 ? (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                  No backend-discovered PM action is currently executable for this submission.
                </div>
              ) : null}

              {selectedDraft.actions.length > 0 ? (
                <div className="space-y-3">
                  <div className="grid gap-2">
                    {selectedDraft.actions.map((action) => (
                      <button
                        key={action.action_code}
                        type="button"
                        className={`rounded-md border px-3 py-2 text-left text-sm transition ${
                          selectedDraft.actionCode === action.action_code
                            ? "border-indigo-500 bg-indigo-50 text-indigo-950"
                            : "border-gray-200 bg-white hover:border-gray-300"
                        }`}
                        onClick={() =>
                          updateDraft(selectedSubmission.submission_id, {
                            actionCode: action.action_code,
                          })
                        }
                      >
                        <div className="font-medium">
                          {formatWorkflowActionLabel(action.action_code)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Moves to {formatWorkflowStatusLabel(action.to_status)}
                        </div>
                      </button>
                    ))}
                  </div>

                  <Textarea
                    placeholder="Required PM intervention reason"
                    value={selectedDraft.reason}
                    onChange={(event) =>
                      updateDraft(selectedSubmission.submission_id, {
                        reason: event.target.value,
                      })
                    }
                  />
                </div>
              ) : null}
            </div>
          </div>

          <div className="border-t px-4 py-3">
            <Button
              className="w-full"
              disabled={
                !selectedDraft.actionCode ||
                !selectedDraft.reason.trim() ||
                selectedDraft.submitting
              }
              onClick={() => submitIntervention(selectedSubmission.submission_id)}
            >
              {selectedDraft.submitting ? "Recording..." : "Record PM Intervention"}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PMDashboard;
